import { Children, type ReactNode } from "react";
import type { ResourceReferenceType } from "@mlx/contracts";

/** Only bare URLs are compacted. Authored labels and inline code stay intact. */
export function isBareLink(href: string, children: ReactNode): boolean {
  const nodes = Children.toArray(children);
  if (!nodes.length || nodes.some((node) => typeof node !== "string"))
    return false;
  const label = nodes.join("").trim();
  // Markdown encodes a bare link's href ({}, spaces, non-ASCII), so compare it decoded too.
  return (
    label === href ||
    label === safeDecodeURI(href) ||
    (label.startsWith("www.") && href === `http://${label}`)
  );
}

/** A workspace link whose content is not plain words (a linked photo, text
 *  mid-wipe) stays itself rather than becoming a chip, in the link colour. */
export const WORKSPACE_LINK_CONTENT_CLASS = "not-typeset text-link no-underline hover:text-link-hover";

/** An author's link text when it is plain words, which a chip can carry. */
export function plainLabel(children: ReactNode): string | null {
  const nodes = Children.toArray(children);
  if (!nodes.length || nodes.some((node) => typeof node !== "string" && typeof node !== "number")) return null;
  const label = nodes.join("").trim();
  return label || null;
}

function safeDecodeURI(value: string): string {
  try {
    return decodeURI(value);
  } catch {
    return value;
  }
}

export function webLinkUrl(href: string, origin?: string): URL | null {
  try {
    const url = new URL(href, origin);
    return ["http:", "https:"].includes(url.protocol) ? url : null;
  } catch {
    return null;
  }
}

export function compactLinkLabel(href: string): string {
  const url = webLinkUrl(href);
  // Keep the real host (including subdomain/port) visible, never URL credentials.
  const host = url?.host ?? "";
  const path = url
    ? `${url.pathname === "/" ? "" : url.pathname}${url.search}${url.hash}`
    : href;
  const available = Math.max(12, 52 - host.length);
  return (
    host + (path.length > available ? `${path.slice(0, available - 1)}…` : path)
  );
}

export function internalLinkResource(
  href: string,
  origin: string,
): {
  type: ResourceReferenceType;
  id: string;
} | null {
  const url = webLinkUrl(href, origin);
  if (!url || url.origin !== origin || url.username || url.password)
    return null;
  const artifact = url.searchParams.get("artifact");
  if (
    artifact &&
    (url.pathname === "/knowledge" ||
      /^\/workspace\/sessions\/[^/]+\/?$/.test(url.pathname))
  ) {
    return { type: "artifact", id: artifact };
  }
  const file = url.pathname === "/knowledge" && url.searchParams.get("file");
  if (file) return { type: "file", id: file };
  const match = /^\/workspace\/(sessions|artifacts)\/([^/]+)\/?$/.exec(
    url.pathname,
  );
  if (!match || match[2] === "new") return null;
  try {
    return {
      type: match[1] === "sessions" ? "session" : "artifact",
      id: decodeURIComponent(match[2]!),
    };
  } catch {
    return null;
  }
}
